import { useState, useEffect } from 'react';
import { CirclePlay, X, BookOpen, Play, ArrowRight, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useHelpResource } from '@/hooks/use-help-resource';
import { usePartnerBranding } from '@/hooks/use-partner-branding';
import { cn } from '@/lib/utils';
import type { NextStep } from '@/config/help-videos';

const W = 'common:helpWidget';

// Loom serves thumbnails under CDN paths whose suffix is not deterministic,
// so resolve the public poster URL via oembed (CORS-enabled) and cache per id.
const loomPosterCache = new Map<string, string | null>();

async function fetchLoomPoster(loomId: string): Promise<string | null> {
  if (loomPosterCache.has(loomId)) return loomPosterCache.get(loomId)!;
  try {
    const res = await fetch(
      `https://www.loom.com/v1/oembed?url=https%3A%2F%2Fwww.loom.com%2Fshare%2F${loomId}`,
    );
    if (!res.ok) throw new Error(`oembed ${res.status}`);
    const data = (await res.json()) as { thumbnail_url?: string };
    const url = data.thumbnail_url ?? null;
    loomPosterCache.set(loomId, url);
    return url;
  } catch {
    loomPosterCache.set(loomId, null);
    return null;
  }
}

function VideoPreview({ title, loomId }: { title: string; loomId?: string }) {
  const [posterUrl, setPosterUrl] = useState<string | null>(
    loomId ? loomPosterCache.get(loomId) ?? null : null,
  );
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => {
    if (!loomId) return;
    if (loomPosterCache.has(loomId)) {
      setPosterUrl(loomPosterCache.get(loomId) ?? null);
      return;
    }
    let cancelled = false;
    void fetchLoomPoster(loomId).then((url) => {
      if (!cancelled) setPosterUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [loomId]);

  const showPoster = !!posterUrl && !posterFailed;

  return (
    <div
      className={cn(
        'group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-md',
        !showPoster && 'bg-gradient-to-br from-violet-500 to-violet-700',
      )}
    >
      {showPoster ? (
        <img
          src={posterUrl}
          alt={title}
          loading="lazy"
          onError={() => setPosterFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      )}

      {showPoster && <div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />}

      <div className="relative flex flex-col items-center gap-2">
        <div className="flex size-12 items-center justify-center rounded-full bg-white/20 shadow-lg backdrop-blur-sm ring-2 ring-white/30 transition-transform group-hover:scale-110">
          <Play className="ml-0.5 size-5 fill-white text-white" />
        </div>
        {!showPoster && <span className="text-xs font-medium text-white/80">{title}</span>}
      </div>
    </div>
  );
}

function useResolveStepPath() {
  const { orgSlug, id: storeId } = useParams();

  return (step: NextStep): string | null => {
    if (step.scope === 'org') {
      if (!orgSlug) return null;
      return `/org/${orgSlug}${step.path}`;
    }
    if (!orgSlug || !storeId) return null;
    return `/org/${orgSlug}/stores/${storeId}${step.path}`;
  };
}

export function HelpVideoWidget() {
  const {
    resource,
    isPermanentlyDismissed,
    collapsed,
    collapse,
    expand,
    dismissPermanently,
  } = useHelpResource();
  const partnerBranding = usePartnerBranding();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolveStepPath = useResolveStepPath();

  useEffect(() => {
    if (resource && !isPermanentlyDismissed) {
      const timer = setTimeout(() => setMounted(true), 100);
      return () => clearTimeout(timer);
    }
    setMounted(false);
  }, [resource, isPermanentlyDismissed]);

  if (partnerBranding) return null;
  if (!resource || isPermanentlyDismissed) return null;

  const title = t(`${W}.titles.${resource.titleKey}`);
  const hasVideo = !!resource.video;
  const hasArticle = !!resource.article;
  const nextSteps = resource.nextSteps ?? [];

  const handleWatch = () => {
    setSheetOpen(true);
    collapse();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissPermanently();
  };

  const handleDismissForever = () => {
    dismissPermanently();
    setSheetOpen(false);
  };

  const handleNextStep = (step: NextStep) => {
    const path = resolveStepPath(step);
    if (!path) return;
    setSheetOpen(false);
    navigate(path);
  };

  function NextStepsList({ steps, compact }: { steps: NextStep[]; compact?: boolean }) {
    if (steps.length === 0) return null;

    return (
      <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
        {!compact && (
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t(`${W}.whatsNext`)}
          </p>
        )}
        {steps.map((step) => {
          const path = resolveStepPath(step);
          if (!path) return null;
          return (
            <button
              key={step.path}
              type="button"
              onClick={() => handleNextStep(step)}
              className={cn(
                'group flex w-full items-center gap-2 rounded-lg text-left transition-colors',
                compact
                  ? 'px-2 py-1.5 hover:bg-accent'
                  : 'border px-3 py-2.5 hover:bg-accent hover:border-accent',
              )}
            >
              <ArrowRight className={cn(
                'shrink-0 text-violet-500',
                compact ? 'size-3' : 'size-3.5',
              )} />
              <span className={cn(
                'flex-1 font-medium',
                compact ? 'text-xs' : 'text-sm',
              )}>
                {t(`${W}.steps.${step.labelKey}`)}
              </span>
              <ChevronRight className={cn(
                'shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100',
                compact ? 'size-3' : 'size-3.5',
              )} />
            </button>
          );
        })}
      </div>
    );
  }

  function SheetBody() {
    return (
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {hasVideo && hasArticle
              ? t(`${W}.descBoth`)
              : hasVideo
                ? t(`${W}.descVideo`)
                : t(`${W}.descArticle`)}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          {hasVideo && (
            <div className="relative w-full overflow-hidden rounded-lg border" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.loom.com/embed/${resource!.video!.loomId}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`}
                allowFullScreen
                className="absolute inset-0 h-full w-full"
                title={title}
              />
            </div>
          )}

          {hasArticle && (
            <a
              href={resource!.article!.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-accent"
            >
              <BookOpen className="size-5 shrink-0 text-muted-foreground" />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{t(`${W}.steps.${resource!.article!.labelKey}`)}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`${W}.openInNewTab`)}
                </span>
              </div>
            </a>
          )}

          {nextSteps.length > 0 && (
            <div className="pt-2">
              <NextStepsList steps={nextSteps} />
            </div>
          )}
        </div>

        <div className="border-t px-4 py-3">
          <button
            type="button"
            onClick={handleDismissForever}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(`${W}.dontShowAgain`)}
          </button>
        </div>
      </SheetContent>
    );
  }

  if (collapsed) {
    return (
      <>
        <button
          onClick={() => setSheetOpen(true)}
          className={cn(
            'fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full',
            'bg-violet-600 pl-3 pr-4 py-2 text-white shadow-lg',
            'transition-all hover:bg-violet-700 hover:shadow-xl hover:scale-105',
            'animate-in fade-in slide-in-from-bottom-2 duration-300',
          )}
        >
          <CirclePlay className="size-4" />
          <span className="text-xs font-medium">{t(`${W}.tutorial`)}</span>
        </button>

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetBody />
        </Sheet>
      </>
    );
  }

  return (
    <>
      <div
        className={cn(
          'fixed bottom-5 right-5 z-40 w-80',
          'transition-all duration-500 ease-out',
          mounted
            ? 'translate-y-0 opacity-100'
            : 'translate-y-4 opacity-0',
        )}
      >
        <div className="overflow-hidden rounded-xl border bg-card shadow-2xl shadow-violet-500/10 dark:shadow-violet-500/5">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-2 right-2 z-10 rounded-full bg-black/40 p-1 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/60 hover:text-white"
          >
            <X className="size-3.5" />
          </button>

          {hasVideo && (
            <button type="button" onClick={handleWatch} className="block w-full">
              <VideoPreview title={title} loomId={resource.video!.loomId} />
            </button>
          )}

          <div className="p-3">
            <div className="flex items-start gap-2">
              <CirclePlay className="mt-0.5 size-4 shrink-0 text-violet-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight text-foreground">{title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t(`${W}.shortVideoGuide`)}
                </p>
              </div>
            </div>

            <div className="mt-2.5 flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleWatch}
                className="flex-1 bg-violet-600 text-white hover:bg-violet-700"
              >
                <Play className="size-3.5" />
                {t(`${W}.watchTutorial`)}
              </Button>
              {hasArticle && (
                <Button size="sm" variant="outline" asChild className="shrink-0">
                  <a href={resource.article!.url} target="_blank" rel="noopener noreferrer">
                    <BookOpen className="size-3.5" />
                  </a>
                </Button>
              )}
            </div>

            {nextSteps.length > 0 && (
              <div className="mt-2 border-t pt-2">
                <NextStepsList steps={nextSteps} compact />
              </div>
            )}
          </div>
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetBody />
      </Sheet>
    </>
  );
}
